import axios from "axios";

// Prefer the current browser origin so requests are always same-origin.
// This works for: preview (lead-hub-web.preview.emergentagent.com), production
// (lead-hub-web.emergent.host), and any custom domain (e.g. lms.losung360.com)
// pointed at the Emergent ingress — eliminating cross-origin / CORS issues.
// Falls back to REACT_APP_BACKEND_URL for non-browser contexts (SSR, tests).
const BASE =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" && window.location && window.location.origin);
export const API = `${BASE}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach bearer token if present (fallback when cookies blocked)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("lms_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On a 401 caused by an expired access token, transparently refresh once (using
// the httpOnly refresh cookie) and retry the original request. This prevents
// hard 401s on actions taken after the 12h access-token TTL lapses — e.g. a
// Send Email dialog left open for a while. Auth endpoints and Gmail-specific
// 401s ("reconnect Gmail") are intentionally excluded so we don't loop or mask
// the real Gmail error.
let _refreshing = null;
api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const { response, config } = error || {};
    if (!response || !config) return Promise.reject(error);
    const url = config.url || "";
    const isAuthEndpoint = url.includes("/auth/refresh") || url.includes("/auth/login");
    const detail = response.data && response.data.detail;
    const detailStr = typeof detail === "string" ? detail : "";
    const isGmail401 = /gmail|reconnect/i.test(detailStr);

    if (response.status === 401 && !config._retry && !isAuthEndpoint && !isGmail401) {
      config._retry = true;
      try {
        if (!_refreshing) {
          _refreshing = axios
            .post(`${API}/auth/refresh`, {}, { withCredentials: true })
            .then((r) => (r.data && r.data.access_token) || null);
        }
        const newToken = await _refreshing;
        _refreshing = null;
        if (newToken) {
          localStorage.setItem("lms_token", newToken);
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${newToken}`;
        }
        return api(config); // retry the original request
      } catch (e) {
        _refreshing = null;
        localStorage.removeItem("lms_token");
        if (typeof window !== "undefined" && window.location && !window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail === "object") {
    if (typeof detail.detail === "string") return detail.detail;
    if (detail.missing_fields && Array.isArray(detail.missing_fields)) {
      const head = detail.detail || "Missing fields";
      return `${head}: ${detail.missing_fields.join(", ")}`;
    }
    if (typeof detail.msg === "string") return detail.msg;
    return JSON.stringify(detail);
  }
  return String(detail);
}

/**
 * Cached GET — for "almost-static" reference data (users, master values, saved views).
 * Returns cached value instantly if present + younger than `ttlMs`, then revalidates
 * in the background and resolves with the fresher result on next call.
 *
 * Use only for safe-to-cache endpoints. Mutations on the same data should call
 * `cachedGet.invalidate(url)` to drop the stale entry.
 */
const _cache = new Map(); // url -> { data, at }

export async function cachedGet(url, { ttlMs = 5 * 60 * 1000 } = {}) {
  const now = Date.now();
  const hit = _cache.get(url);
  if (hit && now - hit.at < ttlMs) {
    return { data: hit.data, cached: true };
  }
  const res = await api.get(url);
  _cache.set(url, { data: res.data, at: now });
  return { data: res.data, cached: false };
}

cachedGet.invalidate = (urlOrPredicate) => {
  if (typeof urlOrPredicate === "function") {
    for (const k of _cache.keys()) if (urlOrPredicate(k)) _cache.delete(k);
  } else {
    _cache.delete(urlOrPredicate);
  }
};

